import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccess } from '../../../hooks/useAccess';
import { useGetActiveLanguages } from '../../../services/redux/api/languagesAPI';
import {
  useArchiveAgreementVersionMutation,
  useCreateAgreementItemMutation,
  useCreateAgreementVersionMutation,
  useDeleteAgreementItemMutation,
  useGetAgreementDocumentItemsByIdQuery,
  useGetAgreementDocumentVersionsQuery,
  usePublishAgreementVersionMutation,
  useReorderAgreementItemsMutation,
  useUpdateAgreementItemMutation,
} from '../../../services/redux/api/agreementsApi';
import {
  agreementsReducer,
  getSelectedVersion,
  initialAgreementsState,
} from './state/agreementsReducer';
import {
  AgreementDocumentType,
  IAgreementItem,
} from '../../../types/interfaces/agreement';
import { errorToastMessage } from '../../../components/layout/toast-root/toast';

export const AGREEMENT_TYPES: AgreementDocumentType[] = [
  'termsOfUse',
  'privacyPolicy',
  'cookiePolicy',
];

const FALLBACK_ITEM_TYPES = [
  'title',
  'paragraph',
  'listItem',
  'link',
  'html',
  'other',
];

function sortItemsByPosition(items: IAgreementItem[]): IAgreementItem[] {
  return [...items].sort((left, right) => {
    const leftPosition =
      typeof left.order === 'number' && Number.isFinite(left.order)
        ? left.order
        : Number.MAX_SAFE_INTEGER;
    const rightPosition =
      typeof right.order === 'number' && Number.isFinite(right.order)
        ? right.order
        : Number.MAX_SAFE_INTEGER;
    if (leftPosition !== rightPosition) return leftPosition - rightPosition;
    return left.id.localeCompare(right.id);
  });
}

export function useAgreementsComponent(
  selectedDocumentType: AgreementDocumentType,
) {
  const { i18n } = useTranslation();
  function notifyMutationError(error: unknown, fallbackMessage: string) {
    const status = (error as any)?.status;
    const responseMessage =
      (error as any)?.data?.message ||
      (error as any)?.error?.message ||
      (error as any)?.message;
    if (status === 409) {
      errorToastMessage(responseMessage || fallbackMessage);
      return;
    }
    if (responseMessage) {
      errorToastMessage(responseMessage);
    }
  }

  const { access } = useAccess();
  const [state, dispatch] = useReducer(
    agreementsReducer,
    initialAgreementsState,
  );
  const [sourceVersionId, setSourceVersionId] = useState<string>('');
  const [draftOrderItems, setDraftOrderItems] = useState<IAgreementItem[]>([]);
  const { data: languages = [] } = useGetActiveLanguages();
  const { currentData: versions = [], isFetching } =
    useGetAgreementDocumentVersionsQuery({
      type: selectedDocumentType,
    });

  const [createVersion, createVersionMeta] =
    useCreateAgreementVersionMutation();
  const [publishVersion, publishVersionMeta] =
    usePublishAgreementVersionMutation();
  const [archiveVersion, archiveVersionMeta] =
    useArchiveAgreementVersionMutation();
  const [updateItem, updateItemMeta] = useUpdateAgreementItemMutation();
  const [createItem, createItemMeta] = useCreateAgreementItemMutation();
  const [deleteItem, deleteItemMeta] = useDeleteAgreementItemMutation();
  const [reorderItems, reorderItemsMeta] = useReorderAgreementItemsMutation();

  useEffect(() => {
    if (!state.selectedLanguageKey && languages.length) {
      const userLanguage = i18n.resolvedLanguage || i18n.language || 'en';
      const matchedLanguage = languages.find(
        (language) => language.key === userLanguage,
      );
      dispatch({
        type: 'SET_SELECTED_LANGUAGE',
        payload: matchedLanguage?.key || languages[0].key,
      });
    }
  }, [
    i18n.language,
    i18n.resolvedLanguage,
    languages,
    state.selectedLanguageKey,
  ]);

  useEffect(() => {
    setSourceVersionId('');
    setDraftOrderItems([]);
  }, [selectedDocumentType]);

  useEffect(() => {
    if (state.selectedType !== selectedDocumentType) {
      dispatch({
        type: 'SET_SELECTED_TYPE',
        payload: selectedDocumentType,
      });
    }
  }, [selectedDocumentType, state.selectedType]);

  const selectedVersion = useMemo(
    () => getSelectedVersion(versions, state.selectedVersionId),
    [versions, state.selectedVersionId],
  );

  useEffect(() => {
    if (selectedVersion && selectedVersion.id !== state.selectedVersionId) {
      dispatch({ type: 'SET_SELECTED_VERSION', payload: selectedVersion.id });
      return;
    }
    if (!selectedVersion && state.selectedVersionId !== null) {
      dispatch({ type: 'SET_SELECTED_VERSION', payload: null });
    }
  }, [selectedVersion, state.selectedVersionId]);

  const { data: selectedDocumentItems } = useGetAgreementDocumentItemsByIdQuery(
    {
      documentId: selectedVersion?.documentId || '',
    },
    {
      skip: !selectedVersion?.documentId,
    },
  );

  const selectedLanguage = useMemo(
    () =>
      languages.find((language) => language.key === state.selectedLanguageKey),
    [languages, state.selectedLanguageKey],
  );

  const blocks = useMemo(
    () => sortItemsByPosition(selectedDocumentItems?.items ?? []),
    [selectedDocumentItems?.items],
  );

  const itemTypeOptions = useMemo(() => {
    const dynamicTypes = Array.from(new Set(blocks.map((item) => item.type)));
    const merged = Array.from(
      new Set([...dynamicTypes, ...FALLBACK_ITEM_TYPES].filter(Boolean)),
    );
    return merged.map((type) => ({ id: type, name: type }));
  }, [blocks]);

  const blocksForSelectedLanguage = useMemo(() => {
    if (!state.selectedLanguageKey) return blocks;
    return blocks.map((item) => ({
      ...item,
      preview:
        item.translations?.find(
          (translation) =>
            translation.languageKey === state.selectedLanguageKey,
        )?.value ?? '',
    }));
  }, [blocks, state.selectedLanguageKey]);

  const canEditVersion = useMemo(() => {
    if (!selectedVersion || !access?.agreements?.canEdit) return false;
    return selectedVersion.actions?.canEdit === true;
  }, [access?.agreements?.canEdit, selectedVersion]);

  const canPublish = useMemo(() => {
    if (!selectedVersion || !access?.agreements?.canEdit) return false;
    return selectedVersion.actions?.canPublish === true;
  }, [access?.agreements?.canEdit, selectedVersion]);

  const canArchive = useMemo(() => {
    if (!selectedVersion || !access?.agreements?.canEdit) return false;
    const fromActions = selectedVersion.actions?.canArchive;
    return fromActions === true;
  }, [access?.agreements?.canEdit, selectedVersion]);

  const canCreateVersion = access?.agreements?.canCreate ?? false;
  const canCreateItem =
    (access?.agreements?.canCreate ?? false) && canEditVersion;
  const canEditItem = (access?.agreements?.canEdit ?? false) && canEditVersion;
  const canDeleteItem =
    (access?.agreements?.canDelete ?? false) && canEditVersion;
  const canReorderItems =
    (access?.agreements?.canEdit ?? false) && canEditVersion;

  async function onCreateVersion() {
    if (!canCreateVersion || !selectedVersion?.documentId) return;
    try {
      const createdVersion = await createVersion({
        documentId: selectedVersion.documentId,
        sourceVersion:
          Number.parseInt(sourceVersionId, 10) || selectedVersion.version,
      }).unwrap();
      if (createdVersion?.id) {
        dispatch({ type: 'SET_SELECTED_VERSION', payload: createdVersion.id });
      }
      dispatch({ type: 'CLOSE_CREATE_VERSION_MODAL' });
      setSourceVersionId('');
    } catch {
      return;
    }
  }

  async function onPublishVersion() {
    if (!canPublish || !selectedVersion?.documentId) return;
    try {
      await publishVersion({ documentId: selectedVersion.documentId }).unwrap();
      dispatch({ type: 'CLOSE_PUBLISH_CONFIRM' });
    } catch (error) {
      notifyMutationError(
        error,
        'Archived agreement document cannot be published directly',
      );
      return;
    }
  }

  async function onArchiveVersion() {
    if (!canArchive || !selectedVersion?.documentId) return;
    try {
      await archiveVersion({ documentId: selectedVersion.documentId }).unwrap();
      dispatch({ type: 'CLOSE_ARCHIVE_CONFIRM' });
    } catch (error) {
      notifyMutationError(
        error,
        'Published agreement document cannot be archived directly',
      );
      return;
    }
  }

  async function onDeleteItem() {
    if (!canDeleteItem || !state.itemForDelete?.id) return;
    try {
      await deleteItem({ itemId: state.itemForDelete.id }).unwrap();
      dispatch({ type: 'CLOSE_DELETE_MODAL' });
    } catch (error) {
      notifyMutationError(error, 'Only draft agreement document can be edited');
      return;
    }
  }

  async function onSaveItem(payload: {
    itemId: string;
    type: string;
    translations: { languageKey: string; value: string }[];
  }) {
    if (!canEditItem || !payload.itemId?.trim() || !payload.type?.trim())
      return;
    const itemId = payload.itemId.trim();
    const type = payload.type.trim();
    try {
      await updateItem({
        itemId,
        body: {
          type,
          translations: payload.translations,
        },
      }).unwrap();
      dispatch({ type: 'CLOSE_EDIT_ITEM_MODAL' });
    } catch (error) {
      notifyMutationError(error, 'Only draft agreement document can be edited');
      return;
    }
  }

  async function onCreateItem(payload: {
    type: string;
    translations: { languageKey: string; value: string }[];
  }) {
    if (!canCreateItem || !selectedVersion?.documentId || !payload.type?.trim())
      return;
    try {
      const maxOrder = blocks.reduce((max, item) => {
        const value = typeof item.order === 'number' ? item.order : -1;
        return Math.max(max, value);
      }, -1);
      await createItem({
        body: {
          documentId: selectedVersion.documentId,
          order: maxOrder + 1,
          type: payload.type.trim(),
          translations: payload.translations,
        },
      }).unwrap();
      dispatch({ type: 'CLOSE_CREATE_ITEM_MODAL' });
    } catch (error) {
      notifyMutationError(error, 'Only draft agreement document can be edited');
      return;
    }
  }

  function onOpenSortModal() {
    if (!canReorderItems) return;
    setDraftOrderItems(sortItemsByPosition(blocks));
    dispatch({ type: 'OPEN_SORT_MODAL' });
  }

  async function onSaveSort() {
    if (!canReorderItems || !selectedVersion?.documentId) return;
    try {
      await reorderItems({
        body: {
          items: draftOrderItems.map((item, index) => ({
            id: item.id,
            order: index + 1,
          })),
        },
      }).unwrap();
      dispatch({ type: 'CLOSE_SORT_MODAL' });
    } catch {
      return;
    }
  }

  useEffect(() => {
    if (!state.isSortModalOpen) return;
    setDraftOrderItems(sortItemsByPosition(blocks));
  }, [state.isSortModalOpen, blocks]);

  return {
    state,
    dispatch,
    isFetching,
    versions,
    selectedVersion,
    languages,
    selectedLanguage,
    blocksForSelectedLanguage,
    itemTypeOptions,
    sourceVersionId,
    setSourceVersionId,
    canPublish,
    canArchive,
    draftOrderItems,
    setDraftOrderItems,
    onCreateVersion,
    onPublishVersion,
    onArchiveVersion,
    onDeleteItem,
    onSaveItem,
    onCreateItem,
    onOpenSortModal,
    onSaveSort,
    canCreateVersion,
    canEditVersion,
    canCreateItem,
    canEditItem,
    canDeleteItem,
    canReorderItems,
    isBusy:
      createVersionMeta.isLoading ||
      publishVersionMeta.isLoading ||
      archiveVersionMeta.isLoading ||
      createItemMeta.isLoading ||
      updateItemMeta.isLoading ||
      deleteItemMeta.isLoading ||
      reorderItemsMeta.isLoading,
  };
}
